// Diario de lotes en vuelo — ADR-2026-09-11-el-pendiente-de-la-tanda-se-escribe-sincronico.
//
// `patchToolCard` NO persiste: encola un `setMessages`. El sessionStorage lo escribe un
// efecto posterior al commit, y la DB va detrás de un debounce de 600ms. Y el
// sessionStorage **no sobrevive cerrar la pestaña**, así que ahí el único respaldo es la
// DB — hasta 600ms atrasada (el flush del unmount es una llamada de red que no termina
// durante el teardown).
//
// En el lote la ventana es peor que en una generación suelta: `pending_generation` se
// patchea DESPUÉS de que `lanzarEnParalelo` terminó, o sea que hasta 18 ítems pueden
// haber salido —y cobrado— sin que su `execution_id` esté guardado en ningún lado. Si la
// pestaña muere ahí, esas imágenes existen y se pagaron, pero la tarjeta vuelve a ofrecer
// "Generar" y las cobra de nuevo.
//
// El diario cierra esa ventana: se escribe SINCRÓNICO en localStorage (que sí sobrevive
// el cierre) ANTES de lanzar y en cada resultado, y la reanudación lo lee junto con el
// input de la tarjeta, quedándose con el que llegó más lejos.
//
// UNA CLAVE POR TARJETA, no un objeto con todas
// (ADR-2026-09-11-el-diario-de-lotes-es-una-clave-por-tarjeta). Con una sola clave, cada
// escritura era leer-modificar-escribir sobre el objeto entero: dos pestañas generando a
// la vez leían las dos el mismo estado y la última en escribir borraba la entrada de la
// otra — justo el respaldo que esto existe para tener. localStorage no da transacciones,
// pero claves distintas no se pisan entre sí.

import type { PendienteLote } from './loteImagenes';

const PREFIJO = 'atlas-v2-lote-en-vuelo:';

const claveDe = (cardId: string) => `${PREFIJO}${cardId}`;

/** Cuánto vale la pena retomar. Más viejo que esto: si salió, está en Creaciones. */
export const EDAD_MAX_DIARIO_MS = 30 * 60 * 1000;

/** La poda recorre todo el localStorage; en una tanda de 18 ítems se escribe 20 veces y
 *  no tiene sentido barrer en cada una. */
const MS_ENTRE_PODAS = 60 * 1000;
let ultimaPoda = 0;

const esVencido = (p: unknown): boolean =>
  !p ||
  typeof (p as PendienteLote).started_at !== 'number' ||
  (p as PendienteLote).started_at < Date.now() - EDAD_MAX_DIARIO_MS;

/** Saca las entradas vencidas. Sólo borra por EDAD, así que es segura entre pestañas: una
 *  entrada recién escrita nunca está vencida, y sobre las vencidas las dos coinciden. */
function podarVencidos(): void {
  const ahora = Date.now();
  if (ahora - ultimaPoda < MS_ENTRE_PODAS) return;
  ultimaPoda = ahora;
  // Las claves primero: borrar mientras se recorre por índice los corre.
  const claves: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PREFIJO)) claves.push(k);
  }
  for (const k of claves) {
    try {
      const raw = localStorage.getItem(k);
      if (esVencido(raw ? JSON.parse(raw) : null)) localStorage.removeItem(k);
    } catch {
      // Ilegible (JSON roto): no se va a poder recuperar nada de ahí.
      localStorage.removeItem(k);
    }
  }
}

/** El pendiente de ESTA tarjeta, o null si no hay o ya venció. */
export function leerPendienteDeLote(cardId: string): PendienteLote | null {
  try {
    const raw = localStorage.getItem(claveDe(cardId));
    if (!raw) return null;
    const p = JSON.parse(raw) as PendienteLote;
    return esVencido(p) ? null : p;
  } catch {
    return null;
  }
}

/** Escribe (o borra, con `null`) el pendiente de una tarjeta. Toca SÓLO su clave: lo que
 *  esté generando otra pestaña queda intacto. */
export function escribirDiarioDeLotes(cardId: string, pendiente: PendienteLote | null): void {
  try {
    if (pendiente) localStorage.setItem(claveDe(cardId), JSON.stringify(pendiente));
    else localStorage.removeItem(claveDe(cardId));
    podarVencidos();
  } catch {
    /* localStorage puede fallar (modo privado, cuota) — non-fatal: se pierde el
       respaldo extra, no la generación. */
  }
}

/** De las dos fuentes, la que llegó MÁS LEJOS (más ítems con estado conocido). El input
 *  de la tarjeta puede haber quedado atrás, o no haberse escrito nunca. */
export function pendienteMasCompleto(
  delInput: PendienteLote | null | undefined,
  delDiario: PendienteLote | null | undefined,
): PendienteLote | null {
  const n = (p?: PendienteLote | null) => (Array.isArray(p?.estados) ? p!.estados.length : -1);
  if (!delInput) return delDiario ?? null;
  if (!delDiario) return delInput;
  return n(delDiario) >= n(delInput) ? delDiario : delInput;
}
